import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  IconX,
  IconClock,
  IconCircleFilled,
  IconCalendarEvent,
  IconChevronRight,
  IconPlus,
} from "@tabler/icons-react";
import { format, isSameDay, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import "./dayEventsModal.css";

const CONFIRMATION_STATUS = {
  pending: { label: "Pendente", color: "#ff9900" },
  canceled: { label: "Cancelado", color: "#dc3545" },
  confirmed: { label: "Confirmado", color: "#28a745" },
};

const DayEventsModal = ({
  show,
  onHide,
  date,
  events = [],
  onEventClick,
  onNewEvent,
  canCreate = false,
  calendars = [],
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!date) return null;

  // parseISO interprets "2026-03-05" as local date, not UTC
  const selectedDate = parseISO(date);

  // Filter real events for this day (exclude free slots)
  const dayEvents = events
    .filter((ev) => {
      if (ev.extendedProps?.isFreeSlot) return false;
      const evStart = new Date(ev.start);
      return isSameDay(evStart, selectedDate);
    })
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  const formattedDate = format(selectedDate, "EEEE, dd 'de' MMMM", {
    locale: ptBR,
  });

  const getCalendarName = (event) => {
    const calId = event.extendedProps?.calendar || event.extendedProps?.calendarId;
    const cal = calendars.find((c) => c.id === calId);
    return cal?.name || "";
  };

  const getStatusInfo = (event) => {
    const status = event.extendedProps?.confirmation || "pending";
    return CONFIRMATION_STATUS[status] || CONFIRMATION_STATUS.pending;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className={`day-events-modal ${isMobile ? "mobile" : "desktop"}`}
      size={isMobile ? undefined : "md"}
    >
      {/* Header */}
      <div className="day-events-header">
        <div className="day-events-header-content">
          <div className="day-events-date">
            <span className="day-events-day-number">
              {format(selectedDate, "dd")}
            </span>
            <div className="day-events-date-text">
              <span className="day-events-weekday">
                {format(selectedDate, "EEEE", { locale: ptBR })}
              </span>
              <span className="day-events-month">
                {format(selectedDate, "MMMM 'de' yyyy", { locale: ptBR })}
              </span>
            </div>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onHide}
            className="day-events-close-btn"
          >
            <IconX size={16} />
          </Button>
        </div>
        <div className="day-events-count">
          {dayEvents.length === 0
            ? "Nenhum agendamento"
            : dayEvents.length === 1
            ? "1 agendamento"
            : `${dayEvents.length} agendamentos`}
        </div>
      </div>

      <Modal.Body className="day-events-body">
        {dayEvents.length === 0 ? (
          <div className="day-events-empty">
            <IconCalendarEvent size={48} color="#ccc" stroke={1.5} />
            <p>Nenhum agendamento para este dia</p>
          </div>
        ) : (
          <div className="day-events-list">
            {dayEvents.map((event) => {
              const statusInfo = getStatusInfo(event);
              const calendarName = getCalendarName(event);
              const startTime = format(new Date(event.start), "HH:mm");
              const endTime = event.end
                ? format(new Date(event.end), "HH:mm")
                : "";

              return (
                <button
                  key={event.id}
                  className="day-event-item"
                  onClick={() => onEventClick(event)}
                >
                  <div
                    className="day-event-color-bar"
                    style={{
                      backgroundColor:
                        event.backgroundColor || "var(--primary)",
                    }}
                  />
                  <div className="day-event-content">
                    <div className="day-event-title">{event.title}</div>
                    <div className="day-event-meta">
                      <span className="day-event-time">
                        <IconClock size={13} />
                        {startTime}
                        {endTime && ` - ${endTime}`}
                      </span>
                      {calendarName && (
                        <span className="day-event-calendar">
                          {calendarName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="day-event-right">
                    <span
                      className="day-event-status"
                      style={{ backgroundColor: statusInfo.color }}
                    >
                      <IconCircleFilled size={8} />
                      {statusInfo.label}
                    </span>
                    <IconChevronRight
                      size={16}
                      className="day-event-chevron"
                    />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Botão novo agendamento */}
        {canCreate && (
        <div className="day-events-new-btn-wrapper">
          <Button
            variant="primary"
            className="day-events-new-btn"
            onClick={() => onNewEvent(date)}
          >
            <IconPlus size={18} className="me-2" />
            Novo Agendamento
          </Button>
        </div>
        )}
      </Modal.Body>

      {!isMobile && (
        <Modal.Footer className="day-events-footer">
          <Button variant="secondary" onClick={onHide}>
            Fechar
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default DayEventsModal;
